(() => {
  const root = document.querySelector('[data-article]');
  if (!root) return;

  const body = root.querySelector('[data-article-body]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slugify = (str) =>
    str.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60) || 'section';

  const tocMounts = Array.from(root.querySelectorAll('[data-article-toc]'));
  let headings = [];

  if (body) {
    headings = Array.from(body.querySelectorAll('h2')).filter(
      (h) => h.textContent.trim().length
    );
  }

  if (tocMounts.length && headings.length >= 2) {
    const used = new Set();
    headings.forEach((h) => {
      if (!h.id) {
        const base = slugify(h.textContent);
        let id = base;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
        h.id = id;
      }
      used.add(h.id);
    });

    const linksById = new Map();

    tocMounts.forEach((mount) => {
      const list = mount.querySelector('.article__toc-list');
      if (!list) return;

      headings.forEach((h) => {
        const li = document.createElement('li');
        li.dataset.level = h.tagName === 'H3' ? '3' : '2';
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent.trim();
        a.addEventListener('click', (e) => {
          e.preventDefault();
          h.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          history.replaceState(null, '', `#${h.id}`);
          if (mount.tagName === 'DETAILS') mount.open = false;
        });
        li.appendChild(a);
        list.appendChild(li);

        if (!linksById.has(h.id)) linksById.set(h.id, []);
        linksById.get(h.id).push(a);
      });

      mount.hidden = false;
    });

    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            linksById.forEach((arr) => arr.forEach((a) => a.classList.remove('is-active')));
            (linksById.get(entry.target.id) || []).forEach((a) => a.classList.add('is-active'));
          }
        });
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach((h) => spy.observe(h));
  }

  const progress = root.querySelector('[data-article-progress]');
  if (progress && body) {
    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = body.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height - vh;
      const scrolled = total > 0 ? (-rect.top) / total : (rect.top <= 0 ? 1 : 0);
      const pct = Math.min(1, Math.max(0, scrolled));
      progress.style.width = `${(pct * 100).toFixed(1)}%`;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // --- FAQ section -> accordion -------------------------------------------------
  // Posts are authored as  section#frequently-asked-questions > h2 > h3 > p,  and the
  // format is still settling, so this reads the structure rather than assuming exact
  // markup: the h2 stays put as the section heading, and every h3 becomes a toggle
  // owning whatever follows it until the next h3.
  //
  // The h3 is kept INSIDE the summary rather than replaced by it. That preserves the
  // heading outline (h2 > h3) that the rest of the theme was just corrected for, and
  // summary's content model explicitly allows heading content.
  //
  // Progressive enhancement: with JS off the raw h3 + p are still there and readable.
  // The FAQPage JSON-LD is built server-side in snippets/article-faq-jsonld.liquid from
  // the raw article.content, so it never sees this transform.
  const faqSection = (body || root).querySelector('section#frequently-asked-questions');
  if (faqSection && !faqSection.dataset.faqEnhanced) {
    const questions = Array.from(faqSection.querySelectorAll(':scope > h3'));

    if (questions.length) {
      const chevron =
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';

      questions.forEach((h3) => {
        // Everything between this h3 and the next one is the answer. Some posts carry an
        // in-body JSON-LD block inside this section; leave those where they are rather
        // than burying them in a toggle.
        const skip = { SCRIPT: 1, STYLE: 1, TEMPLATE: 1, NOSCRIPT: 1 };
        const answer = [];
        let node = h3.nextElementSibling;
        while (node && node.tagName !== 'H3') {
          const next = node.nextElementSibling;
          if (!skip[node.tagName]) answer.push(node);
          node = next;
        }
        if (!answer.length) return;

        const details = document.createElement('details');
        details.className = 'article-faq__item';

        const summary = document.createElement('summary');
        summary.className = 'article-faq__q';

        h3.parentNode.insertBefore(details, h3);
        summary.appendChild(h3);
        summary.insertAdjacentHTML('beforeend', chevron);
        details.appendChild(summary);

        const wrap = document.createElement('div');
        wrap.className = 'article-faq__a rte';
        answer.forEach((el) => wrap.appendChild(el));
        details.appendChild(wrap);
      });

      faqSection.classList.add('article-faq');
      faqSection.dataset.faqEnhanced = 'true';

      // A link to a question inside a collapsed toggle would otherwise scroll to nothing.
      const revealFromHash = () => {
        if (!location.hash) return;
        let target;
        try {
          target = faqSection.querySelector(location.hash);
        } catch (e) {
          return; // hash is not a valid selector
        }
        if (!target) return;
        const owner = target.closest('details.article-faq__item');
        if (owner) owner.open = true;
      };
      revealFromHash();
      window.addEventListener('hashchange', revealFromHash);
    }
  }

  const cardTpl = root.querySelector('[data-article-inline-card]');
  if (cardTpl && body) {
    const h2s = body.querySelectorAll('h2');
    const anchor = h2s[1] || h2s[0];
    if (anchor) {
      const node = cardTpl.content.firstElementChild.cloneNode(true);
      anchor.parentNode.insertBefore(node, anchor);
    }
  }
})();
